import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2, OnChanges } from '@angular/core';
import { Subject } from 'rxjs';

@Directive({
    selector: '[appHighlightField]',
    standalone: true
})
export class HighlightFieldDirective implements OnInit, OnDestroy, OnChanges {
    @Input() public appHighlightField: boolean = false;

    private destroy$ = new Subject<void>();
    private focusListener?: () => void;
    private blurListener?: () => void;

    public constructor(
        private elementRef: ElementRef<HTMLElement>,
        private renderer: Renderer2
    ) {}

    public ngOnInit() {
        this.setupListeners();
        this.updateHighlight();
    }

    public ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.removeListeners();
    }

    public ngOnChanges() {
        this.updateHighlight();
    }

    private setupListeners() {
        const element = this.elementRef.nativeElement;

        this.focusListener = () => {
            if (this.appHighlightField) {
                this.renderer.addClass(element, 'highlight-focused');
                this.renderer.removeClass(element, 'highlight-animated');
            }
        };

        this.blurListener = () => {
            if (this.appHighlightField) {
                this.renderer.removeClass(element, 'highlight-focused');
                this.renderer.addClass(element, 'highlight-animated');
            }
        };

        // Handle different types of elements
        const focusableElement = this.getFocusableElement(element);

        if (focusableElement) {
            this.renderer.listen(focusableElement, 'focus', this.focusListener);
            this.renderer.listen(focusableElement, 'blur', this.blurListener);
        }

        // Also listen on the container for custom components
        this.renderer.listen(element, 'focusin', this.focusListener);
        this.renderer.listen(element, 'focusout', this.blurListener);
    }

    private removeListeners() {
        // Listeners are automatically cleaned up by Renderer2
    }

    private getFocusableElement(element: HTMLElement): HTMLElement | null {
        // Check if element itself is focusable
        if (this.isFocusable(element)) {
            return element;
        }

        // Look for focusable children
        const focusableSelectors = [
            'input',
            'textarea',
            'select',
            'button',
            '[tabindex]:not([tabindex="-1"])',
            '.ng-select'
        ];

        for (const selector of focusableSelectors) {
            const child = element.querySelector(selector);
            if (child) {
                return child as HTMLElement;
            }
        }

        return null;
    }

    private isFocusable(element: HTMLElement): boolean {
        const focusableTags = ['input', 'textarea', 'select', 'button'];
        return (
            focusableTags.includes(element.tagName.toLowerCase()) ||
            element.hasAttribute('tabindex') ||
            element.classList.contains('ng-select')
        );
    }

    private updateHighlight() {
        const element = this.elementRef.nativeElement;

        if (this.appHighlightField) {
            this.renderer.addClass(element, 'highlight');
            this.renderer.addClass(element, 'highlight-animated');
            this.renderer.removeClass(element, 'highlight-focused');
        } else {
            this.renderer.removeClass(element, 'highlight');
            this.renderer.removeClass(element, 'highlight-animated');
            this.renderer.removeClass(element, 'highlight-focused');
        }
    }
}
